import { useCallback, useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";

export interface PdfSearchMatch {
  pageNum: number;
  startIndex: number;
  length: number;
}

export function usePdfSearch() {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<PdfSearchMatch[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [isExtracting, setIsExtracting] = useState(false);

  // Cached page texts (1-indexed: pageTexts[1] = text of page 1)
  const pageTextsRef = useRef<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const extractText = useCallback(async (pdf: PDFDocumentProxy) => {
    setIsExtracting(true);
    const texts: string[] = [""];  // index 0 unused, 1-indexed
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items
        .filter((item) => "str" in item)
        .map((item) => (item as { str: string }).str)
        .join("");
      texts.push(pageText);
    }
    pageTextsRef.current = texts;
    setIsExtracting(false);
  }, []);

  // Run search whenever query changes (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setMatches([]);
      setCurrentMatchIndex(-1);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const results: PdfSearchMatch[] = [];
      const lowerQuery = query.toLowerCase();
      const texts = pageTextsRef.current;

      // Try exact match first (works for short queries and manual searches)
      for (let pageNum = 1; pageNum < texts.length; pageNum++) {
        const pageText = texts[pageNum].toLowerCase();
        let pos = 0;
        while (true) {
          const idx = pageText.indexOf(lowerQuery, pos);
          if (idx === -1) break;
          results.push({ pageNum, startIndex: idx, length: lowerQuery.length });
          pos = idx + 1;
        }
      }

      // If no exact match, try whitespace-stripped matching with index mapping.
      // PDF text extraction joins text items without separators, so "the quick"
      // in the source becomes "thequick" in the extracted text. We strip
      // whitespace from both sides and build a mapping to recover original indices.
      if (results.length === 0 && lowerQuery.length > 10) {
        const strippedQuery = lowerQuery.replace(/\s+/g, "");
        for (let pageNum = 1; pageNum < texts.length; pageNum++) {
          const pageText = texts[pageNum].toLowerCase();
          // Build stripped text + index map: strippedIndexMap[i] = original index
          const strippedChars: string[] = [];
          const indexMap: number[] = [];
          for (let i = 0; i < pageText.length; i++) {
            if (!/\s/.test(pageText[i])) {
              strippedChars.push(pageText[i]);
              indexMap.push(i);
            }
          }
          const strippedPage = strippedChars.join("");
          let sPos = 0;
          while (true) {
            const idx = strippedPage.indexOf(strippedQuery, sPos);
            if (idx === -1) break;
            // Map back to original page text indices
            const originalStart = indexMap[idx];
            const originalEnd = indexMap[idx + strippedQuery.length - 1] + 1;
            results.push({
              pageNum,
              startIndex: originalStart,
              length: originalEnd - originalStart,
            });
            sPos = idx + 1;
          }
        }
      }

      setMatches(results);
      setCurrentMatchIndex(results.length > 0 ? 0 : -1);
    }, 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const nextMatch = useCallback(() => {
    setCurrentMatchIndex((prev) => {
      if (matches.length === 0) return -1;
      return (prev + 1) % matches.length;
    });
  }, [matches.length]);

  const prevMatch = useCallback(() => {
    setCurrentMatchIndex((prev) => {
      if (matches.length === 0) return -1;
      return (prev - 1 + matches.length) % matches.length;
    });
  }, [matches.length]);

  const clearSearch = useCallback(() => {
    setQuery("");
    setMatches([]);
    setCurrentMatchIndex(-1);
  }, []);

  const currentMatch = currentMatchIndex >= 0 ? matches[currentMatchIndex] : null;

  return {
    query,
    setQuery,
    matches,
    currentMatchIndex,
    currentMatch,
    nextMatch,
    prevMatch,
    clearSearch,
    extractText,
    isExtracting,
    pageTexts: pageTextsRef,
  };
}
